import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaWallet, FaArrowDown, FaTimes, FaExclamationCircle,
  FaCheckCircle, FaSync, FaUniversity, FaRupeeSign
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const InfluenerWallet = () => {
  const [walletData, setWalletData] = useState({ balance: 0, transactions: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  const fetchWalletData = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/get/wallet/detail`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      setWalletData({
        balance: response.data.balance || 0,
        transactions: response.data.transactions || [],
      });
    } catch (err) {
      setError("Failed to load wallet data");
    }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      await fetchWalletData();
      setLoading(false);
    };
    load();
  }, []);

  const totalPages = Math.ceil(walletData.transactions.length / rowsPerPage);
  const paginatedTransactions = [...walletData.transactions].reverse().slice(
    (currentPage - 1) * rowsPerPage, currentPage * rowsPerPage
  );

  const getStatusStyle = (status) => {
    if (!status) return "bg-slate-100 text-slate-600";
    const s = status.toLowerCase();
    if (s === "completed" || s === "credit") return "bg-green-100 text-green-700";
    if (s === "pending") return "bg-amber-100 text-amber-700";
    if (s === "failed" || s === "debit") return "bg-red-100 text-red-600";
    return "bg-slate-100 text-slate-600";
  };

  const isDebit = (t) => t.type?.toLowerCase() === "debit" || t.status?.toLowerCase() === "debit";

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 border-4 border-slate-200 border-t-purple-600 rounded-full animate-spin" />
        <p className="text-sm font-bold text-slate-500 uppercase tracking-widest">Loading Wallet...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* ── Hero Balance Card ─────────────────────────── */}
      <div className="bg-gradient-to-br from-slate-900 via-purple-950 to-slate-900 px-4 pt-6 pb-20">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center justify-between mb-5">
            <div>
              <p className="text-[10px] font-black text-purple-400 uppercase tracking-widest">Influencer Wallet</p>
              <h1 className="text-base font-black text-white">My Financials</h1>
            </div>
            <button
              onClick={() => { setLoading(true); fetchWalletData().finally(() => setLoading(false)); }}
              className="w-9 h-9 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <FaSync className="w-3.5 h-3.5 text-white/70" />
            </button>
          </div>

          {/* Balance Pill */}
          <div className="bg-white/10 backdrop-blur-sm border border-white/10 rounded-2xl p-5 mb-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-purple-500/30 flex items-center justify-center">
                <FaWallet className="w-4 h-4 text-purple-300" />
              </div>
              <div>
                <p className="text-[9px] font-black text-purple-400 uppercase tracking-widest">Available Balance</p>
                <p className="text-2xl font-black text-white">₹{Number(walletData.balance).toFixed(2)}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => navigate("/influencer/withdrawal")}
                className="flex-1 flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 text-white text-xs font-black py-2.5 rounded-xl transition-colors"
              >
                <FaArrowDown className="w-3 h-3" /> Withdraw Request
              </button>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white/10 border border-white/10 rounded-xl p-3 text-center">
              <p className="text-lg font-black text-green-400">
                ₹{walletData.transactions.filter(t => !isDebit(t)).reduce((s, t) => s + Math.abs(t.amount || 0), 0).toFixed(2)}
              </p>
              <p className="text-[9px] font-black text-purple-400 uppercase tracking-widest mt-0.5">Total Earnings</p>
            </div>
            <div className="bg-white/10 border border-white/10 rounded-xl p-3 text-center">
              <p className="text-lg font-black text-red-400">
                ₹{walletData.transactions.filter(t => isDebit(t)).reduce((s, t) => s + Math.abs(t.amount || 0), 0).toFixed(2)}
              </p>
              <p className="text-[9px] font-black text-purple-400 uppercase tracking-widest mt-0.5">Total Withdrawals</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Transaction List ──────────────────────────── */}
      <div className="max-w-lg mx-auto px-3 -mt-10 pb-8">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <div>
              <h2 className="text-sm font-black text-slate-900">Transaction History</h2>
              <p className="text-[10px] text-slate-400 font-bold">{walletData.transactions.length} total activities</p>
            </div>
            <span className="text-[9px] font-black text-purple-600 bg-purple-50 px-2.5 py-1 rounded-full uppercase tracking-widest">
              Page {currentPage}/{totalPages || 1}
            </span>
          </div>

          {paginatedTransactions.length === 0 ? (
            <div className="py-16 flex flex-col items-center gap-2">
              <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center mb-2">
                <FaWallet className="w-5 h-5 text-slate-400" />
              </div>
              <p className="text-sm font-black text-slate-900">No transactions yet</p>
              <p className="text-xs text-slate-400">Your earnings will appear here.</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-50">
              {paginatedTransactions.map((txn, idx) => (
                <div key={idx} className="px-5 py-3 flex items-center gap-3 hover:bg-slate-50/70 transition-colors">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${isDebit(txn) ? "bg-red-100" : "bg-green-100"}`}>
                    <FaRupeeSign className={`w-3.5 h-3.5 ${isDebit(txn) ? "text-red-500" : "text-green-600"}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-slate-900 truncate capitalize">{txn.description || txn.type || "Transaction"}</p>
                    <p className="text-[9px] text-slate-400 font-bold mt-0.5">
                      {new Date(txn.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                    </p>
                    <span className={`inline-block text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full mt-1 ${getStatusStyle(txn.status)}`}>
                      {txn.status || txn.type}
                    </span>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className={`text-sm font-black ${isDebit(txn) ? "text-red-600" : "text-green-600"}`}>
                      {isDebit(txn) ? "-" : "+"}₹{Math.abs(txn.amount || 0).toFixed(2)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="border-t border-slate-100 px-5 py-3 flex items-center justify-between bg-slate-50/60">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="text-xs font-black text-purple-600 disabled:text-slate-300 uppercase tracking-widest transition-colors"
              >← Prev</button>
              <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="text-xs font-black text-purple-600 disabled:text-slate-300 uppercase tracking-widest transition-colors"
              >Next →</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InfluenerWallet;
